import React, { useLayoutEffect, useState } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import {
	LoadingOverlay,
	TextInput,
	Title,
	Text,
	useMantineTheme,
	PasswordInput,
	Checkbox,
	Notification,
	Button,
	Box,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useSearchParams } from 'react-router-dom';
import { HeroImg } from '@/components/partials/heroImg/HeroImg';
import { AppPaths } from '@/configs/appPaths';
import { ErrorCode, useContent } from '@/providers/ContentProvider';
import { isAppError } from '@/utils/utilities';
import { useMutation } from '@tanstack/react-query';
import UnauthenticatedApi from '@/api/UnauthenticatedApi';
import styles from './Login.module.scss';

export interface LoginValues {
	email: string;
	password: string;
}

export enum SubmitStatus {
	THROTTLE,
	LOADING,
	ERROR,
	SUCCESS,
}

const initialValues = {
	email: '',
	password: '',
};

export type AvailableSocials = 'google' | 'facebook';

const baseSlug = 'login.';

export const Login: React.FC = () => {
	const { getErrorMessage, getContent } = useContent();

	const { other } = useMantineTheme();
	const { remember, setRemember, login } = useAuth();

	const [status, setStatus] = useState(SubmitStatus.THROTTLE);
	const [resultMessage, setResultMessage] = useState('');

	const [searchParams, setSearchParams] = useSearchParams();

	const form = useForm({
		initialValues,
		validate: {
			email: (value) => {
				if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
					return getErrorMessage([ErrorCode.INVALID_EMAIL]);
				}
				return null;
			},
		},
	});

	const verifyRegistrationMutation = useMutation({
		mutationFn: UnauthenticatedApi.verifyEmailToken,
		onSuccess: () => {
			setSearchParams({});
			setStatus(SubmitStatus.SUCCESS);
			setResultMessage(getContent(`${baseSlug}verifyEmailSuccess`));
		},
		onError: (error) => {
			const isError = isAppError(error);
			if (!isError) return;

			setStatus(SubmitStatus.ERROR);
			setResultMessage(getErrorMessage(error.stack));
		},
	});

	useLayoutEffect(() => {
		const hash = searchParams.get('token');
		if (!hash) return;

		verifyRegistrationMutation.mutate(hash);
	}, []);

	const handleSubmit = form.onSubmit(async (loginValues: LoginValues) => {
		setStatus(SubmitStatus.LOADING);
		setResultMessage('');

		const result = await login(loginValues);
		form.reset();

		if (result.status === SubmitStatus.ERROR) {
			setStatus(SubmitStatus.ERROR);
			setResultMessage(getErrorMessage(result.stack));
			return;
		}

		setStatus(SubmitStatus.THROTTLE);
	});

	return (
		<Box className="page-wrapper-unauth">
			<form className={styles.form} onSubmit={handleSubmit}>
				<LoadingOverlay visible={status === SubmitStatus.LOADING} />
				<Title ta="center" order={1} mb="md">
					{getContent(`${baseSlug}title`)}
				</Title>
				<Text size="lg" ta="center" c="dimmed" mb="xl">
					{getContent(`${baseSlug}description`)}
				</Text>
				{status === SubmitStatus.ERROR && (
					<Notification
						{...other.notificationProps.danger}
						mb="lg"
						onClose={() => setStatus(SubmitStatus.THROTTLE)}
					>
						<Text size="md">
							{resultMessage || getContent(`${baseSlug}generalError`)}
						</Text>
					</Notification>
				)}
				{status === SubmitStatus.SUCCESS && (
					<Notification
						{...other.notificationProps.success}
						mb="lg"
						onClose={() => setStatus(SubmitStatus.THROTTLE)}
					>
						<Text size="md">{resultMessage}</Text>
					</Notification>
				)}
				<TextInput
					{...form.getInputProps('email')}
					required
					type="email"
					placeholder={getContent(`emailPlaceholder`)}
					mb="lg"
				/>
				<PasswordInput
					{...form.getInputProps('password')}
					required
					placeholder={getContent(`passwordPlaceholder`)}
					mb="lg"
				/>
				<Checkbox
					label={getContent(`${baseSlug}checkboxLabel`)}
					mb="lg"
					checked={remember}
					onChange={({ target }) => setRemember(target.checked)}
				/>
				<Button type="submit" mb="lg" w="100%">
					{getContent(`${baseSlug}submitButton`)}
				</Button>
				<Box ta="center" c="dimmed" mb="md">
					{getContent(`${baseSlug}navigateResetPassword`, {
						links: {
							$RESET_PASSWORD: {
								to: `/${AppPaths.RESET_PASSWORD}`,
								className: 'injected-anchor',
							},
						},
					})}
				</Box>
				<Box ta="center" c="dimmed" mb="md">
					{getContent(`${baseSlug}navigateRegister`, {
						links: {
							$REGISTER: {
								to: `/${AppPaths.REGISTER}`,
								className: 'injected-anchor',
							},
						},
					})}
				</Box>
			</form>
			<HeroImg src={getContent(`${baseSlug}heroImg`)} />
		</Box>
	);
};
